import React, { createContext, useContext, useState, useEffect, useRef } from 'react';

const NotificacionesContext = createContext();

export const useNotificaciones = () => {
  const context = useContext(NotificacionesContext);
  if (!context) {
    throw new Error('useNotificaciones debe usarse dentro de NotificacionesProvider');
  }
  return context;
};

export const NotificacionesProvider = ({ children }) => {
  const [pedidosNuevos, setPedidosNuevos] = useState(0);
  const [cotizacionesNuevas, setCotizacionesNuevas] = useState(0);
  const [ultimaRevision, setUltimaRevision] = useState(Date.now());
  
  const audioCtxRef = useRef(null);
  const prevPedidosRef = useRef(0);
  const prevCotizacionesRef = useRef(0);

  // Inicializar audio context
  const initAudio = () => {
    if (!audioCtxRef.current) {
      try {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      } catch {
        audioCtxRef.current = null;
      }
    }
  };

  // Reproducir sonido de notificación
  const playNotificationSound = () => {
    const ctx = audioCtxRef.current;
    if (!ctx) return;
    
    // Melodía de notificación (3 tonos ascendentes)
    const notes = [
      { freq: 800, duration: 100, delay: 0 },
      { freq: 1000, duration: 100, delay: 120 },
      { freq: 1200, duration: 150, delay: 240 }
    ];

    notes.forEach(note => {
      setTimeout(() => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = note.freq;
        gain.gain.value = 0.2;
        osc.connect(gain).connect(ctx.destination);
        osc.start();
        osc.stop(ctx.currentTime + note.duration / 1000);
      }, note.delay);
    });
  };

  // Verificar nuevos pedidos y cotizaciones
  const verificarNuevos = async () => {
    try {
      // Verificar pedidos nuevos
      const respPedidos = await fetch('/api/pedidos?estado=nuevo');
      if (respPedidos.ok) {
        const pedidos = await respPedidos.json();
        const nuevosPedidos = pedidos.length;
        
        // Si hay más pedidos que antes, reproducir sonido
        if (nuevosPedidos > prevPedidosRef.current && prevPedidosRef.current > 0) {
          initAudio();
          playNotificationSound();
        }
        
        setPedidosNuevos(nuevosPedidos);
        prevPedidosRef.current = nuevosPedidos;
      }

      // Verificar cotizaciones nuevas del e-commerce
      const respCotizaciones = await fetch('/api/ecommerce/cotizaciones');
      if (respCotizaciones.ok) {
        const cotizaciones = await respCotizaciones.json();
        const nuevasCotizaciones = cotizaciones.filter(c => c.estado === 'pendiente').length;
        
        // Si hay más cotizaciones que antes, reproducir sonido
        if (nuevasCotizaciones > prevCotizacionesRef.current && prevCotizacionesRef.current > 0) {
          initAudio();
          playNotificationSound();
        }
        
        setCotizacionesNuevas(nuevasCotizaciones);
        prevCotizacionesRef.current = nuevasCotizaciones;
      }

      setUltimaRevision(Date.now());
    } catch (e) {
      console.error('Error al verificar notificaciones:', e);
    }
  };

  // Verificar cada 30 segundos
  useEffect(() => {
    // Verificar inmediatamente al cargar
    verificarNuevos();

    // Luego verificar cada 30 segundos
    const interval = setInterval(verificarNuevos, 30000);

    return () => clearInterval(interval);
  }, []);

  const marcarPedidosVistos = () => {
    setPedidosNuevos(0);
    prevPedidosRef.current = 0;
  };

  const marcarCotizacionesVistas = () => {
    setCotizacionesNuevas(0);
    prevCotizacionesRef.current = 0;
  };

  const refrescarNotificaciones = () => {
    verificarNuevos();
  };

  return (
    <NotificacionesContext.Provider value={{
      pedidosNuevos,
      cotizacionesNuevas,
      marcarPedidosVistos,
      marcarCotizacionesVistas,
      refrescarNotificaciones,
      ultimaRevision
    }}>
      {children}
    </NotificacionesContext.Provider>
  );
};
